/**
 * Role 15 — Statement of Comprehensive Income — calculated totals.
 *
 * Extracted verbatim from the former single-class `CalculationService`, which held
 * all 13 sheets' total calculations in one 5,557-line file. Each function is pure and
 * self-contained (no shared state, no `this`), so the split is a move, not a rewrite.
 * `CalculationService` remains the injectable entry point and delegates here.
 */

/**
 * Calculate all Role15 (410000 - Statement of Comprehensive Income) totals based on form data
 *
 * @param formData - The form data object with control values (format: qname[member][period])
 * @param xbrlPrefix - The XBRL prefix ('ifrs-full' for MFRS, 'ifrs-smes' for MPERS)
 * @returns Object containing only the calculated total fields
 */
export function calculateTotals_410000(
  formData: Record<string, any>,
  xbrlPrefix: 'ifrs-full' | 'ifrs-smes',
): Record<string, any> {
  const calculatedTotals: Record<string, any> = {};

  // Helper function to get field value from formData
  const getFieldValue = (qname: string, suffix: string): number => {
    const value = formData[qname + suffix];

    // Handle null, undefined, or empty string
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    // Convert to number if it's a string
    const numValue = typeof value === 'number' ? value : parseFloat(value);

    // Return 0 if conversion resulted in NaN
    return isNaN(numValue) ? 0 : numValue;
  };

  // Helper function to set calculated value
  const setValue = (qname: string, suffix: string, value: number): void => {
    calculatedTotals[qname + suffix] = value;
  };

  const isMFRS = xbrlPrefix === 'ifrs-full';

  const suffixes = [
    '[Consolidated][CurrentYear]',
    '[Consolidated][PreviousYear]',
    '[Separate][CurrentYear]',
    '[Separate][PreviousYear]',
  ];

  suffixes.forEach((suffix) => {
    if (isMFRS) {
      // #region "MFRS"
      // 1. Calculated subtotal: Other items (NOT reclassified) = sum of 3 fields
      const otherItemsNotReclassified =
        getFieldValue(
          'ifrs-full:OtherComprehensiveIncomeNetOfTaxGainsLossesOnRevaluation',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:OtherComprehensiveIncomeNetOfTaxGainsLossesOnRemeasurementsOfDefinedBenefitPlans',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:OtherComprehensiveIncomeNetOfTaxGainsLossesFromInvestmentsInEquityInstruments',
          suffix,
        );

      setValue(
        'ifrs-full:OtherComprehensiveIncomeNetOfTaxOtherEquityInstrumentsThatWillNotBeReclassifiedToProfitOrLoss',
        suffix,
        otherItemsNotReclassified,
      );

      // 2. Total NOT reclassified = the "other equity instruments" subtotal
      //    (just computed above) + Share of OCI.
      //    Use the LOCAL `otherItemsNotReclassified` rather than reading the
      //    subtotal back via getFieldValue: `setValue` writes to
      //    `calculatedTotals`, but `getFieldValue` reads `formData` only, so on
      //    the SINGLE-PASS hydration call the read-back returned 0 and dropped
      //    this subtotal — under-computing totalOCI (e.g. 208.68 instead of
      //    211.74) and persisting it to modified-state until the role-15 editor
      //    re-ran the calc reactively and self-healed. That stale value broke
      //    SOCIE's cross-sheet OtherComprehensiveIncome equality (red X on the
      //    dropdown until SOCI was opened) and is the root of the long-standing
      //    "Attribution mismatch Calculated=… , Attribution=…" warnings below.
      const totalNotReclassified =
        otherItemsNotReclassified +
        getFieldValue(
          'ifrs-full:ShareOfOtherComprehensiveIncomeOfAssociatesAndJointVenturesAccountedForUsingEquityMethodThatWillNotBeReclassifiedToProfitOrLossNetOfTax',
          suffix,
        );

      setValue(
        'ifrs-full:OtherComprehensiveIncomeThatWillNotBeReclassifiedToProfitOrLossNetOfTax',
        suffix,
        totalNotReclassified,
      );

      // 3. Cash flow hedges subtotal
      const cashFlowHedges =
        getFieldValue(
          'ifrs-full:GainsLossesOnCashFlowHedgesNetOfTax',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:ReclassificationAdjustmentsOnCashFlowHedgesNetOfTax',
          suffix,
        );

      setValue(
        'ifrs-full:OtherComprehensiveIncomeNetOfTaxCashFlowHedges',
        suffix,
        cashFlowHedges,
      );

      // 4. Hedges of net investments subtotal
      const hedgesOfNetInvestments =
        getFieldValue(
          'ifrs-full:GainsLossesOnHedgesOfNetInvestmentsInForeignOperationsNetOfTax',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:ReclassificationAdjustmentsOnHedgesOfNetInvestmentsInForeignOperationsNetOfTax',
          suffix,
        );

      setValue(
        'ifrs-full:OtherComprehensiveIncomeNetOfTaxHedgesOfNetInvestmentsInForeignOperations',
        suffix,
        hedgesOfNetInvestments,
      );

      // 5. Exchange differences subtotal
      const exchangeDifferences =
        getFieldValue(
          'ifrs-full:GainsLossesOnExchangeDifferencesOnTranslationNetOfTax',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:ReclassificationAdjustmentsOnExchangeDifferencesOnTranslationNetOfTax',
          suffix,
        );

      setValue(
        'ifrs-full:OtherComprehensiveIncomeNetOfTaxExchangeDifferencesOnTranslation',
        suffix,
        exchangeDifferences,
      );

      // 6. FVOCI subtotal
      const fvoci =
        getFieldValue(
          'ifrs-full:GainsLossesOnFinancialAssetsMeasuredAtFairValueThroughOtherComprehensiveIncomeNetOfTax',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:ReclassificationAdjustmentsOnFinancialAssetsMeasuredAtFairValueThroughOtherComprehensiveIncomeNetOfTax',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:AmountsRemovedFromEquityAndAdjustedAgainstFairValueOfFinancialAssetsOnReclassificationOutOfFairValueThroughOtherComprehensiveIncomeMeasurementCategoryNetOfTax',
          suffix,
        );

      setValue(
        'ifrs-full:OtherComprehensiveIncomeNetOfTaxFinancialAssetsMeasuredAtFairValueThroughOtherComprehensiveIncome',
        suffix,
        fvoci,
      );

      // 7. Total WILL BE reclassified = all subtotals + Share of OCI + Other items
      const totalWillBeReclassified =
        cashFlowHedges +
        hedgesOfNetInvestments +
        exchangeDifferences +
        fvoci +
        getFieldValue(
          'ifrs-full:ShareOfOtherComprehensiveIncomeOfAssociatesAndJointVenturesAccountedForUsingEquityMethodThatWillBeReclassifiedToProfitOrLossNetOfTax',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:OtherComprehensiveIncomeNetOfTaxGainsLossesOnOtherItems',
          suffix,
        );

      setValue(
        'ifrs-full:OtherComprehensiveIncomeThatWillBeReclassifiedToProfitOrLossNetOfTax',
        suffix,
        totalWillBeReclassified,
      );

      // 8. Total OCI = Total NOT + Total WILL BE
      const totalOCI = totalNotReclassified + totalWillBeReclassified;
      setValue('ifrs-full:OtherComprehensiveIncome', suffix, totalOCI);

      // 9. Total comprehensive income = Profit/Loss + Total OCI
      const profitLoss = getFieldValue('ifrs-full:ProfitLoss', suffix);
      const totalComprehensiveIncome = profitLoss + totalOCI;
      setValue(
        'ifrs-full:ComprehensiveIncome',
        suffix,
        totalComprehensiveIncome,
      );

      // 10. Total comprehensive income attribution (Owners + NCI)
      // This is the SECOND occurrence of ComprehensiveIncome in the schema
      // Uses the _2 suffix as defined in the schema (ifrs-full:ComprehensiveIncome_2)
      const comprehensiveIncomeToOwners = getFieldValue(
        'ifrs-full:ComprehensiveIncomeAttributableToOwnersOfParent',
        suffix,
      );
      const comprehensiveIncomeToNCI = getFieldValue(
        'ifrs-full:ComprehensiveIncomeAttributableToNoncontrollingInterests',
        suffix,
      );
      const totalAttribution =
        comprehensiveIncomeToOwners + comprehensiveIncomeToNCI;

      // Verify attribution matches calculated total (validation)
      if (Math.abs(totalComprehensiveIncome - totalAttribution) > 0.01) {
        console.warn(
          `⚠️ Attribution mismatch for ${suffix}: ` +
            `Calculated=${totalComprehensiveIncome}, Attribution=${totalAttribution}`,
        );
      }

      // Set attribution total (this is displayed in the second row)
      setValue('ifrs-full:ComprehensiveIncome_2', suffix, totalAttribution);
      // #endregion "MFRS"
    } else {
      // #region "MPERS"
      // 1. Total other comprehensive income that will NOT be reclassified to profit or loss, net of tax
      const totalNotReclassified =
        getFieldValue(
          'ifrs-smes:OtherComprehensiveIncomeNetOfTaxExchangeDifferencesOnTranslation',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:OtherComprehensiveIncomeNetOfTaxActuarialGainsLossesOnDefinedBenefitPlans',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:OtherComprehensiveIncomeNetOfTaxGainsLossesOnRevaluation',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:ShareOfOtherComprehensiveIncomeOfAssociatesAndJointVenturesAccountedForUsingEquityMethodThatWillNotBeReclassifiedToProfitOrLossNetOfTax',
          suffix,
        );

      setValue(
        'ssmt-mpers:OtherComprehensiveIncomeThatWillNotBeReclassifiedToProfitOrLossNetOfTax',
        suffix,
        totalNotReclassified,
      );

      // 2. Total other comprehensive income that WILL BE reclassified to profit or loss, net of tax
      const totalWillBeReclassified =
        getFieldValue(
          'ssmt-mpers:OtherComprehensiveIncomeNetOfTaxExchangeDifferencesOnTranslation',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:OtherComprehensiveIncomeNetOfTaxChangeInFairValueOfHedgingInstrument',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:ShareOfOtherComprehensiveIncomeOfAssociatesAndJointVenturesAccountedForUsingEquityMethodThatWillBeReclassifiedToProfitOrLossNetOfTax',
          suffix,
        );

      setValue(
        'ssmt-mpers:OtherComprehensiveIncomeThatWillBeReclassifiedToProfitOrLossNetOfTax',
        suffix,
        totalWillBeReclassified,
      );

      // 3. Total other comprehensive income (includes Other Items)
      const otherItems = getFieldValue(
        'ssmt-mfrs:OtherComprehensiveIncomeNetOfTaxGainsLossesOnOtherItems',
        suffix,
      );
      const totalOCI =
        totalNotReclassified + totalWillBeReclassified + otherItems;
      setValue('ifrs-smes:OtherComprehensiveIncome', suffix, totalOCI);

      // 4. Total comprehensive income (Profit/Loss + Total OCI)
      const profitLoss = getFieldValue('ifrs-smes:ProfitLoss', suffix);
      const totalComprehensiveIncome = profitLoss + totalOCI;
      setValue(
        'ifrs-smes:ComprehensiveIncome',
        suffix,
        totalComprehensiveIncome,
      );

      // 5. Total comprehensive income attribution (Owners + NCI)
      // This is the SECOND occurrence of ComprehensiveIncome in the MPERS schema
      // Uses the _2 suffix as defined in the schema (ifrs-smes:ComprehensiveIncome_2)
      const comprehensiveIncomeToOwners = getFieldValue(
        'ifrs-smes:ComprehensiveIncomeAttributableToOwnersOfParent',
        suffix,
      );
      const comprehensiveIncomeToNCI = getFieldValue(
        'ifrs-smes:ComprehensiveIncomeAttributableToNoncontrollingInterests',
        suffix,
      );
      const totalAttribution =
        comprehensiveIncomeToOwners + comprehensiveIncomeToNCI;

      // Verify attribution matches calculated total (validation)
      if (Math.abs(totalComprehensiveIncome - totalAttribution) > 0.01) {
        console.warn(
          `⚠️ MPERS Attribution mismatch for ${suffix}: ` +
            `Calculated=${totalComprehensiveIncome}, Attribution=${totalAttribution}`,
        );
      }

      // Set attribution total (this is displayed in the second row)
      setValue('ifrs-smes:ComprehensiveIncome_2', suffix, totalAttribution);
      // #endregion "MPERS"
    }
  });

  return calculatedTotals;
}
